import { useCallback, useContext, useEffect, useMemo } from 'react'

import { Grid, Stack, Tooltip } from '@mui/material'
import Decimal from 'decimal.js'
import { Path, useFieldArray, useFormContext, useWatch } from 'react-hook-form'
import { toast } from 'react-toastify'
import { useDebounce } from 'use-debounce'

import { CardTitle, TabPanelBootstrap, FplDynamicTabs } from 'components/common'
import {
  DEBOUNCE_DELAY,
  DEFAULT_CUSTOMER_WAIT_AND_RETURN_FEE_CATEGORY_ID,
  DeliveryIntervals,
  IgnoredXeroServiceCodes,
  Roles,
} from 'constants/index'
import {
  BookingAddressType,
  BookingStatus,
  GoogleDistanceStatus,
  useDeleteBookingAddressMutation,
  useDeleteBookingPackageMutation,
  useGetDistanceCalculationLazyQuery,
  GetDistanceCalculationQuery,
} from 'generated/graphql'
import { isPageLoadingVar } from 'graphql/reactiveVariables'
import {
  calculateBusinessTransitInterval,
  calculateTransitInterval,
  getGoogleAddressInput,
  getReturnLegAddressInput,
} from 'helpers'
import usePrevious from 'helpers/usePrevious'
import {
  buildReturnCollection,
  buildReturnDelivery,
  canGenerateReturnCollection,
  getNextSequenceOrder,
  getReturnStopFieldUpdates,
  getUserStops,
  isGeneratedReturnStop,
  isReturnCollection,
  isReturnDelivery,
} from 'helpers/waitAndReturn.helpers'
import { useUserRole } from 'hooks'
import { usePermission } from 'providers'
import { FillIntervalParams, TBookingAddressForm, TBookingForm } from 'types'

import { AddressContext } from './AddressProvider'
import { BookingAddress, getBookingAddressDefaultValues } from './BookingAddress'
import { DistanceInput } from './DistanceInput'

interface IProps {
  isQuote: boolean
  isQuickQuote: boolean
  isUpdate?: boolean
  hoverText: string
  disabled?: boolean
  currentWaitAndReturnFeeCategoryId?: number
  currentIsWaitAndReturn?: boolean
}
const DeliveryDetails = (props: IProps) => {
  const {
    isQuote,
    isQuickQuote,
    isUpdate,
    hoverText,
    disabled,
    currentIsWaitAndReturn,
    currentWaitAndReturnFeeCategoryId,
  } = props
  const userRole = useUserRole()

  const { collectionTabValue, setCollectionTabValue, deliveryTabValue, setDeliveryTabValue } =
    useContext(AddressContext)

  const {
    control,
    getValues,
    setValue,
    getFieldState,
    formState: { errors },
  } = useFormContext<TBookingForm>()
  const {
    fields: deliveryFields,
    append,
    remove,
  } = useFieldArray({
    control,
    name: 'deliveryAddresses',
    keyName: 'tabsKey',
  })

  const [collectionAt] = useDebounce(useWatch({ name: 'collectionAddresses.0.at' }), DEBOUNCE_DELAY)
  const [collectionBy] = useDebounce(useWatch({ name: 'collectionAddresses.0.by' }), DEBOUNCE_DELAY)
  const serviceType = useWatch({ name: 'serviceType' })
  const isWaitAndReturn = useWatch({ name: 'isWaitAndReturn' })
  const prevIsWaitAndReturn = usePrevious<boolean>(isWaitAndReturn)
  // The status is only set once a saved booking has been loaded into the form. A previous status
  // therefore tells a tick made on the page from the value arriving with the loaded booking.
  const status = useWatch({ name: 'status' })
  const prevStatus = usePrevious<BookingStatus | undefined>(status)
  const [collectionAddresses, deliveryAddresses] = useWatch({
    name: ['collectionAddresses', 'deliveryAddresses'],
  })
  const fieldsLength = deliveryFields.length
  const prevFieldsLength = usePrevious<number>(deliveryFields.length)

  const [isAllowedToUpdateBookingAfterQuoteIsBooked] = usePermission(
    'UpdateBookingAfterQuoteIsBooked',
  )

  const showAddAndRemoveButtons = isQuote || isAllowedToUpdateBookingAfterQuoteIsBooked
  const showAddButton = showAddAndRemoveButtons && !isWaitAndReturn
  const showRemoveButton = showAddAndRemoveButtons

  const isWaitAndReturnModified = useMemo(() => {
    if (userRole !== Roles.customer) return false

    return (
      currentIsWaitAndReturn &&
      currentWaitAndReturnFeeCategoryId !== undefined &&
      currentWaitAndReturnFeeCategoryId !== Number(DEFAULT_CUSTOMER_WAIT_AND_RETURN_FEE_CATEGORY_ID)
    )
  }, [userRole, currentIsWaitAndReturn, currentWaitAndReturnFeeCategoryId])

  // Queries
  const handleDistanceCalculation = useCallback(
    (data: GetDistanceCalculationQuery, distanceType: 'distance' | 'returnDistance') => {
      const { status, distance: rawDistance, errorMessage } = data.distanceCalculation

      if (status === GoogleDistanceStatus.Success) {
        const distance = new Decimal(rawDistance).toDP(2, Decimal.ROUND_FLOOR).toNumber()

        setValue(distanceType, distance, { shouldDirty: true, shouldValidate: true })
      } else if (status === GoogleDistanceStatus.Error) {
        toast.warning(errorMessage)
      }
    },
    [setValue],
  )

  const [getDistanceCalculation, { loading: distanceCalculationLoading }] =
    useGetDistanceCalculationLazyQuery({
      onError: (error) => {
        toast.error(error.message)
      },
    })

  // Mutations
  const [deleteBookingAddress] = useDeleteBookingAddressMutation()
  const [deleteBookingPackage] = useDeleteBookingPackageMutation()

  const deliveryAddressesErrors = errors?.deliveryAddresses
  const errorTabs = useMemo(
    () =>
      (deliveryAddressesErrors as any)?.map((value, index) => (value !== undefined ? index : null)),
    [deliveryAddressesErrors],
  )

  // Handle isWaitAndReturn logic: ticking generates two locked stops, the return collection at the
  // delivery address and the return delivery at the collection address; unticking removes both.
  // Generated stops are identified by their marker, never by address matching.
  useEffect(() => {
    if (isWaitAndReturn === prevIsWaitAndReturn) return

    const collectionAddrs = getValues('collectionAddresses') ?? []
    const deliveryAddrs = getValues('deliveryAddresses') ?? []
    const userCollection = getUserStops(collectionAddrs)[0]
    const userDelivery = getUserStops(deliveryAddrs)[0]

    const recalculateDistance = (
      collections: Array<TBookingAddressForm>,
      deliveries: Array<TBookingAddressForm>,
    ) => {
      const googleInput = getGoogleAddressInput(collections, deliveries)
      const isWaitAndReturnValue = getValues('isWaitAndReturn') ?? false

      if (googleInput.length >= 2) {
        getDistanceCalculation({ variables: { input: googleInput } }).then(
          ({ data }) => data && handleDistanceCalculation(data, 'distance'),
        )

        // Finds the leg leading into the Wait & Return stop, wherever it appears in the route.
        const returnLeg = isWaitAndReturnValue
          ? getReturnLegAddressInput(collections, deliveries)
          : null

        if (returnLeg) {
          getDistanceCalculation({ variables: { input: returnLeg } }).then(
            ({ data }) => data && handleDistanceCalculation(data, 'returnDistance'),
          )
        } else {
          setValue('returnDistance', 0, { shouldDirty: true })
        }
      }
    }

    const addReturnStops = () => {
      if (!userCollection || !userDelivery) return

      const hasReturnCollection = collectionAddrs.some(isReturnCollection)
      const hasReturnDelivery = deliveryAddrs.some(isReturnDelivery)

      // Stops are generated only when the user switches Wait & Return on in this page, never when a
      // saved booking opens. Switching it off and on again brings the value back to what was loaded,
      // so the field is not dirty; that case is recognised by Wait & Return having been off on an
      // already loaded booking.
      const isWaitAndReturnSetByUser =
        getFieldState('isWaitAndReturn').isDirty ||
        (prevIsWaitAndReturn === false && prevStatus !== undefined)

      if (!isWaitAndReturnSetByUser) {
        // A booking saved with a return collection keeps its stored distances, so that opening it
        // does not mark it as changed. A booking saved before the return collection existed keeps
        // the older behaviour of recalculating both distances when it is opened.
        if (!hasReturnCollection && hasReturnDelivery) {
          recalculateDistance(collectionAddrs, deliveryAddrs)
        }
        return
      }

      // Generation is allowed while the booking is still editable; finished, invoiced or declined
      // bookings are left alone.
      const canAddCollection =
        !hasReturnCollection && canGenerateReturnCollection(getValues('status'))

      if (!canAddCollection && hasReturnDelivery) return

      let nextSequence = getNextSequenceOrder(collectionAddrs, deliveryAddrs)
      const collections = canAddCollection
        ? [
            ...collectionAddrs,
            buildReturnCollection(
              userDelivery,
              getBookingAddressDefaultValues(nextSequence),
              nextSequence++,
            ),
          ]
        : collectionAddrs
      const deliveries = hasReturnDelivery
        ? deliveryAddrs
        : [
            ...deliveryAddrs,
            buildReturnDelivery(
              userCollection,
              getBookingAddressDefaultValues(nextSequence),
              nextSequence,
            ),
          ]

      if (collections !== collectionAddrs) {
        setValue('collectionAddresses', collections, { shouldDirty: true })
      }
      if (deliveries !== deliveryAddrs) {
        setValue('deliveryAddresses', deliveries, { shouldDirty: true })
      }
      // On an older booking only the return collection is generated, at the delivery address the
      // route already visits, so the stored distances stay valid and no re-price prompt is needed.
      if (!hasReturnDelivery || !getValues('returnDistance')) {
        recalculateDistance(collections, deliveries)
      }
    }

    const removeReturnStops = async () => {
      const generated = [...collectionAddrs, ...deliveryAddrs].filter(isGeneratedReturnStop)
      if (!generated.length) return

      // A saved stop cannot be deleted while a consignment points at it, so the return
      // consignment (and any consignment the user pointed at a generated stop) goes first.
      const generatedSequences = generated.map((address) => address.sequenceOrder)
      const packages = getValues('packages') ?? []
      const referencesGenerated = (value: number | string) =>
        value !== '' && generatedSequences.includes(Number(value))
      const returnPackages = packages.filter(
        (pkg) =>
          referencesGenerated(pkg.collectionSequenceOrder) ||
          referencesGenerated(pkg.deliverySequenceOrder),
      )

      const restoreWaitAndReturn = () => setValue('isWaitAndReturn', true, { shouldDirty: false })

      for (const pkg of returnPackages.filter((item) => item.id)) {
        const result = await deleteBookingPackage({ variables: { packageId: Number(pkg.id) } })
        if (!result.data?.deleteBookingPackage) {
          restoreWaitAndReturn()
          return
        }
      }

      if (returnPackages.length) {
        setValue(
          'packages',
          packages.filter((pkg) => !returnPackages.includes(pkg)),
          { shouldDirty: true },
        )
      }

      for (const target of generated.filter((address) => address.id)) {
        const result = await deleteBookingAddress({
          variables: { bookingAddressId: Number(target.id) },
        })
        if (!result.data?.deleteBookingAddress) {
          restoreWaitAndReturn()
          return
        }
      }

      const deliveries = getUserStops(deliveryAddrs)
      const collections = getUserStops(collectionAddrs)

      if (deliveryTabValue >= deliveries.length) {
        setDeliveryTabValue(Math.max(0, deliveries.length - 1))
      }
      if (collectionTabValue >= collections.length) {
        setCollectionTabValue(Math.max(0, collections.length - 1))
      }

      setValue('collectionAddresses', collections, { shouldValidate: true, shouldDirty: true })
      setValue('deliveryAddresses', deliveries, { shouldValidate: true, shouldDirty: true })
      recalculateDistance(collections, deliveries)
    }

    if (isWaitAndReturn) {
      addReturnStops()
    } else if (prevIsWaitAndReturn) {
      removeReturnStops()
    }
  }, [
    collectionTabValue,
    deleteBookingAddress,
    deleteBookingPackage,
    deliveryTabValue,
    getDistanceCalculation,
    getFieldState,
    getValues,
    prevStatus,
    handleDistanceCalculation,
    isWaitAndReturn,
    prevIsWaitAndReturn,
    setCollectionTabValue,
    setDeliveryTabValue,
    setValue,
  ])

  // While Wait & Return is ticked, an edit to a copied field of the collection or the delivery is
  // carried over to the generated stop built from it. Only fields the user changed on this page
  // count, so a loaded booking is left as it is.
  useEffect(() => {
    if (!isWaitAndReturn) return

    getReturnStopFieldUpdates(collectionAddresses, deliveryAddresses)
      .filter((update) => getFieldState(update.sourcePath as Path<TBookingForm>).isDirty)
      .forEach((update) =>
        setValue(update.targetPath as Path<TBookingForm>, update.value, { shouldDirty: true }),
      )
  }, [collectionAddresses, deliveryAddresses, getFieldState, isWaitAndReturn, setValue])

  useEffect(() => {
    if (fieldsLength < Number(prevFieldsLength)) {
      const collectionAddressesValue = getValues('collectionAddresses')
      const deliveryAddressesValue = getValues('deliveryAddresses')

      if (collectionAddressesValue && deliveryAddressesValue) {
        const isWaitAndReturn = getValues('isWaitAndReturn') ?? false

        const googleAddressInput = getGoogleAddressInput(
          collectionAddressesValue,
          deliveryAddressesValue,
        )

        getDistanceCalculation({ variables: { input: googleAddressInput } }).then(
          ({ data }) => data && handleDistanceCalculation(data, 'distance'),
        )

        const returnLeg = isWaitAndReturn
          ? getReturnLegAddressInput(collectionAddressesValue, deliveryAddressesValue)
          : null

        if (returnLeg) {
          getDistanceCalculation({ variables: { input: returnLeg } }).then(
            ({ data }) => data && handleDistanceCalculation(data, 'returnDistance'),
          )
        } else {
          setValue('returnDistance', 0, { shouldDirty: true })
        }
      }
    }
  }, [
    fieldsLength,
    getDistanceCalculation,
    getValues,
    handleDistanceCalculation,
    prevFieldsLength,
    setValue,
  ])

  useEffect(() => {
    // is valid xero service type for auto pop values
    if (!serviceType || IgnoredXeroServiceCodes.includes(serviceType)) {
      return
    }

    const collectionAddresses = getValues('collectionAddresses')
    const deliveryAddresses = getValues('deliveryAddresses')

    // is not multi-drop
    if (collectionAddresses.length + deliveryAddresses.length !== 2) {
      return
    }

    const deliveryInterval = DeliveryIntervals[serviceType]

    if (!deliveryInterval) {
      return
    }

    const intervalParams: FillIntervalParams = {
      transitInterval: deliveryInterval,
      collectionAt: collectionAt ? new Date(collectionAt) : undefined,
      collectionBy: collectionBy ? new Date(collectionBy) : undefined,
      deliveryAddress: deliveryAddresses[0],
      onDeliveryAtSet: (deliveryAt: Date) => setValue('deliveryAddresses.0.at', deliveryAt),
      onDeliveryBySet: (deliveryBy: Date) => setValue('deliveryAddresses.0.by', deliveryBy),
    }

    if (deliveryInterval.workingPeriod) {
      calculateBusinessTransitInterval(intervalParams)
    } else {
      calculateTransitInterval(intervalParams)
    }
  }, [serviceType, getValues, setValue, collectionAt, collectionBy])

  useEffect(() => {
    isPageLoadingVar(distanceCalculationLoading)
  }, [distanceCalculationLoading])

  const handleTabChange = (_, newValue) => {
    setDeliveryTabValue(newValue)
  }

  const handleAddClick = () => {
    const newTabValue = deliveryFields.length
    const collectionAddressesValue = getValues('collectionAddresses')
    const deliveryAddressesValue = getValues('deliveryAddresses')
    const newSequenceOrder =
      [...collectionAddressesValue, ...deliveryAddressesValue].reduce(
        (max, address) => (address.sequenceOrder > max ? address.sequenceOrder : max),
        0,
      ) + 1

    const newAddress = getBookingAddressDefaultValues(newSequenceOrder)

    if (deliveryAddressesValue[0]?.reasonCodeId)
      newAddress.reasonCodeId = deliveryAddressesValue[0].reasonCodeId

    append(newAddress)
    setDeliveryTabValue(newTabValue)
  }

  const handleRemoveClick = (e, index: number, id?: string) => {
    e.stopPropagation()

    // A generated return stop cannot be removed on its own: removing it unticks Wait & Return,
    // which removes the other generated stop as well.
    const deliveryAddressesValue = getValues('deliveryAddresses')
    const isReturnAddress = isReturnDelivery(deliveryAddressesValue[index])

    if (isReturnAddress && isWaitAndReturnModified) {
      toast.error('The delivery address related to Wait & Return cannot be removed.')
      return
    }

    if (id) {
      deleteBookingAddress({ variables: { bookingAddressId: Number(id) } }).then((result) => {
        if (!result.data?.deleteBookingAddress) {
          return
        }

        if (deliveryTabValue === deliveryFields.length - 1) {
          setDeliveryTabValue(deliveryFields.length - 2)
        }
        remove(index)

        // If removing a return address and isWaitAndReturn is true, uncheck it
        if (isReturnAddress && isWaitAndReturn) {
          setValue('isWaitAndReturn', false)
        }

        toast.success('Address was deleted with success')
      })
    } else {
      if (deliveryTabValue === deliveryFields.length - 1) {
        setDeliveryTabValue(deliveryFields.length - 2)
      }
      remove(index)

      // If removing a return address and isWaitAndReturn is true, uncheck it
      if (isReturnAddress && isWaitAndReturn) {
        setValue('isWaitAndReturn', false)
      }
    }
  }

  const handleFindOrAf = () => {
    const collectionAddressesValue = getValues('collectionAddresses')
    const deliveryAddressesValue = getValues('deliveryAddresses')

    if (collectionAddressesValue && deliveryAddressesValue) {
      const googleAddressInput = getGoogleAddressInput(
        collectionAddressesValue,
        deliveryAddressesValue,
      )

      if (googleAddressInput.length >= 2) {
        const isWaitAndReturn = getValues('isWaitAndReturn') ?? false

        getDistanceCalculation({ variables: { input: googleAddressInput } }).then(
          ({ data }) => data && handleDistanceCalculation(data, 'distance'),
        )

        const returnLeg = isWaitAndReturn
          ? getReturnLegAddressInput(collectionAddressesValue, deliveryAddressesValue)
          : null

        if (returnLeg) {
          getDistanceCalculation({ variables: { input: returnLeg } }).then(
            ({ data }) => data && handleDistanceCalculation(data, 'returnDistance'),
          )
        } else {
          setValue('returnDistance', 0, { shouldDirty: true })
        }
      }
    }
  }

  const isReturnAddress = (addressIndex: number) =>
    isReturnDelivery(getValues('deliveryAddresses')?.[addressIndex])

  return (
    <Stack spacing={1}>
      <Grid container justifyContent='space-between' rowSpacing={1}>
        <Grid item>
          <CardTitle marginBottom={0} disabled={disabled}>
            Delivery
          </CardTitle>
        </Grid>

        <Grid item ml='auto'>
          <DistanceInput isQuickQuote={isQuickQuote} disabled={disabled} />
        </Grid>
      </Grid>

      <Stack>
        <FplDynamicTabs
          tabValue={deliveryTabValue}
          fields={deliveryFields}
          name='delivery-address'
          label='Add'
          disabled={disabled}
          showAddButton={showAddButton}
          showRemoveButton={showRemoveButton}
          errorTabs={errorTabs}
          onTabChange={handleTabChange}
          onAddClick={handleAddClick}
          onRemoveClick={handleRemoveClick}
        />

        <Tooltip title={disabled ? hoverText : ''} followCursor>
          <div>
            {deliveryFields.map((item, index) => (
              <TabPanelBootstrap value={deliveryTabValue} index={index} key={item.tabsKey}>
                <BookingAddress
                  bookingAddressType={BookingAddressType.Delivery}
                  disabled={disabled}
                  disableAllExceptAtBy={isReturnAddress(index)}
                  isQuote={isQuote}
                  isQuickQuote={isQuickQuote}
                  isUpdate={isUpdate}
                  item={item}
                  index={index}
                  key={item.tabsKey}
                  onFindOrAf={handleFindOrAf}
                />
              </TabPanelBootstrap>
            ))}
          </div>
        </Tooltip>
      </Stack>
    </Stack>
  )
}

export default DeliveryDetails
